import React, { useEffect, useState } from "react";

/**
 * Cinematic yacht-on-water animation for the opening of the deck.
 * - Wide silhouette of a luxury yacht drifting slowly across the horizon.
 * - Ember particles rising from it (the burning aftermath).
 * - Reflection / water shimmer line beneath.
 * - Fire glow at the hull.
 *
 * Placed inside the Hero section as a decorative atmospheric layer.
 */
export default function BoatAnimation() {
  // Generate a stable ember field once on mount
  const [embers] = useState(() =>
    Array.from({ length: 18 }).map((_, i) => ({
      id: i,
      left: 30 + Math.random() * 40, // percent across hero
      delay: Math.random() * 6,
      duration: 5 + Math.random() * 5,
      size: 1 + Math.random() * 2.5,
      drift: (Math.random() - 0.5) * 30,
    }))
  );

  return (
    <div
      className="pointer-events-none absolute inset-x-0 z-[1] overflow-hidden"
      style={{ bottom: "10%", height: "260px" }}
      aria-hidden
    >
      {/* Soft horizon glow at sea level */}
      <div
        className="absolute left-0 right-0"
        style={{
          bottom: "60px",
          height: "120px",
          background:
            "radial-gradient(ellipse at 50% 100%, rgba(255,140,40,0.18), rgba(255,80,20,0.08) 40%, transparent 75%)",
          filter: "blur(8px)",
        }}
      />

      {/* Drifting yacht */}
      <div className="boat-drift absolute" style={{ bottom: "70px" }}>
        <div className="relative">
          {/* fire glow under hull */}
          <div
            className="absolute"
            style={{
              left: "20px",
              right: "20px",
              bottom: "-10px",
              height: "44px",
              background:
                "radial-gradient(ellipse at 50% 100%, rgba(255,170,60,0.55), rgba(255,90,30,0.18) 40%, transparent 70%)",
              filter: "blur(10px)",
              animation: "fire-flicker 1.6s ease-in-out infinite",
            }}
          />
          {/* yacht SVG silhouette */}
          <svg
            width="320"
            height="90"
            viewBox="0 0 320 90"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <linearGradient id="hullGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3a2a14" />
                <stop offset="55%" stopColor="#1a1108" />
                <stop offset="100%" stopColor="#0a0604" />
              </linearGradient>
              <linearGradient id="windowGlow" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#ffcc55" stopOpacity="0.8" />
                <stop offset="100%" stopColor="#c8801f" stopOpacity="0.2" />
              </linearGradient>
            </defs>

            {/* upper deck */}
            <path
              d="M70 38 L 240 38 L 232 22 L 90 22 Z"
              fill="url(#hullGrad)"
              stroke="#1a0e05"
              strokeWidth="0.5"
            />
            {/* upper cabin */}
            <rect x="130" y="6" width="60" height="18" fill="#16100a" stroke="#1a0e05" strokeWidth="0.5" />
            <rect x="138" y="11" width="44" height="6" fill="url(#windowGlow)" />

            {/* main hull */}
            <path
              d="M40 58 L 280 58 L 260 76 L 60 76 Z"
              fill="url(#hullGrad)"
              stroke="#1a0e05"
              strokeWidth="0.6"
            />
            {/* hull window strip */}
            <rect x="80" y="44" width="160" height="6" fill="url(#windowGlow)" opacity="0.7" />

            {/* antennae */}
            <line x1="150" y1="6" x2="150" y2="-6" stroke="#2a1b0e" strokeWidth="0.7" />
            <line x1="170" y1="6" x2="170" y2="-2" stroke="#2a1b0e" strokeWidth="0.7" />

            {/* small flag */}
            <line x1="150" y1="-6" x2="160" y2="-3" stroke="#c8a14a" strokeWidth="0.8" />
          </svg>

          {/* smoke plume rising */}
          <div
            className="absolute"
            style={{
              left: "150px",
              top: "-90px",
              width: "60px",
              height: "120px",
              background:
                "radial-gradient(ellipse at 50% 100%, rgba(40,30,20,0.5), transparent 70%)",
              filter: "blur(10px)",
              animation: "smoke-rise 4s ease-in-out infinite",
            }}
          />
        </div>
      </div>

      {/* Water shimmer line */}
      <div
        className="absolute left-0 right-0"
        style={{
          bottom: "68px",
          height: "2px",
          background:
            "linear-gradient(90deg, transparent 5%, rgba(200,161,74,0.35) 50%, transparent 95%)",
          filter: "blur(1px)",
        }}
      />
      <div
        className="absolute left-0 right-0"
        style={{
          bottom: "50px",
          height: "1px",
          background:
            "linear-gradient(90deg, transparent 15%, rgba(239,227,204,0.18) 50%, transparent 85%)",
        }}
      />

      {/* Rising embers */}
      {embers.map((e) => (
        <div
          key={e.id}
          className="absolute"
          style={{
            left: `${e.left}%`,
            bottom: "70px",
            width: `${e.size}px`,
            height: `${e.size}px`,
            borderRadius: "50%",
            background: "rgba(255,170,60,0.95)",
            boxShadow: "0 0 6px rgba(255,170,60,0.85)",
            animation: `ember-rise ${e.duration}s linear ${e.delay}s infinite`,
            ["--drift"]: `${e.drift}px`,
          }}
        />
      ))}
    </div>
  );
}
